import { BRAND_KEY, LABEL_KEY } from './const'
import { UserInfo } from './../redux/slices/userSlice'
import { WSAPI } from '@smartico/public-api/dist/WSAPI/WSAPI'

interface TrackerParams {
	brand_key?: string
	debug?: boolean
	server?: string
	simulation_mode?: boolean
}

interface SmarticoClearParams {
	keepCallBacks: boolean
}

interface SmarticoAPI {
	init: (apiKey: string, params: TrackerParams) => void
	identify: (ext_user_id: string, hash?: string, payload?: any) => void
	login: (payload?: any) => void
	logout: (payload?: any) => void
	event: (eventType: string, payload?: any) => void
	setAvatar: (url: string) => void
	dp: (url: string) => void
	miniGame: (saw_template_id: string | number, params?: { iframe: string; inline?: boolean; fullscreen?: boolean }) => void
	action: (action: string) => void
	clear: (params?: SmarticoClearParams) => void
	on: (event: string, callback: null | ((...args: any[]) => void)) => void
	showBanner: (params: { placement_key: string; dom_id: string }) => void
	gamificationBlock: (params: { dom_id: string; url: string; fit?: 'content' | 'fill' }) => void
	tracker: {
		ext_user_id: string
	}
	api: WSAPI
}

export interface ResponseIdentify {
	user_id: number
	ext_user_id: string
	public_username: string
	avatar_id: string
	job: boolean
	props?: { [key: string]: any }
}

interface PersonalDetails {
	firstName?: string
	lastName?: string
	playerEMail?: string
	currency?: string
	core_registration_date?: number
}

class Smartico {
	private static _identified: boolean = false
	private static _apiReadyResolve: ((api: WSAPI) => void) | null = null
	public static apiReady: Promise<WSAPI>

	static initializeApiReady(): void {
		Smartico.apiReady = new Promise((resolve) => {
			Smartico._apiReadyResolve = resolve
		})

		Smartico.checkApi()
	}

	/**
	 * The SDK swaps its api object when the player is identified again (a new demo player is identified twice, with a
	 * clear() in between). The first object then keeps an empty label setup: getInboxMessageBody fetched
	 * "undefined<guid>.json" for about a minute. So apiReady hands out a forwarder that always calls the current object.
	 */
	private static liveApi = new Proxy({} as WSAPI, {
		get: (_target, key) => {
			const api = Smartico.lib().api as any
			const value = api[key]
			return typeof value === 'function' ? value.bind(api) : value
		},
	})

	private static checkApi(): void {
		if (Smartico.lib() && Smartico.lib().api) {
			if (Smartico._apiReadyResolve) {
				Smartico._apiReadyResolve(Smartico.liveApi)
				Smartico._apiReadyResolve = null
			}
		} else {
			setTimeout(() => Smartico.checkApi(), 10)
		}
	}

	public static lib(): SmarticoAPI {
		return (window as any)._smartico
	}

	public static identified(): boolean {
		return this._identified
	}

	private static _onReadyCallback: ((message: ResponseIdentify) => void) | null = null

	public static onReady(callback: (message: ResponseIdentify) => void): void {
		this._onReadyCallback = callback
	}

	private static sendPersonalDetails(details: PersonalDetails): void {
		this.lib().event('demo_personal_details_update', details)
	}

	public static identify(user: UserInfo, depth = 0): void {
		this._identified = true

		this.lib().init(LABEL_KEY, { brand_key: BRAND_KEY })

		this.lib().on('identify', (errCode: number, props: any, message: ResponseIdentify) => {
			this.lib().on('identify', null)
			if (message.ext_user_id.endsWith(message.public_username) && depth < 5) {
				this.sendPersonalDetails({
					firstName: user.profile.firstName || user.profile.lastName || user.email.split('@')[0],
					currency: 'EUR',
					core_registration_date: new Date().getTime(),
					...(user.profile.lastName && { lastName: user.profile.lastName }),
					...(user.email && { playerEMail: user.email }),
				})

				if (user.profile.picture) {
					this.lib().setAvatar(user.profile.picture)
				}

				setTimeout(() => {
					this.clear({ keepCallBacks: true })
					this.identify(user, depth + 1)
				}, 1000)
				return
			}

			if (user.email) {
				this.sendPersonalDetails({ playerEMail: user.email })
			}

			Smartico.action('final-login')
			if (this._onReadyCallback) {
				this._onReadyCallback(message)
				this._onReadyCallback = null
			}
		})

		this.lib().identify(user.user_ext_id)
		this.lib().login()
	}

	public static clear = (params?: SmarticoClearParams) => {
		this._identified = false
		if (this.lib()) {
			this.lib().clear(params)
		}
	}

	public static dp(dp: string) {
		Smartico.lib().dp(dp)
	}

	public static action(action: 'final-login' | 'gf-started' | 'gf-closed') {
		Smartico.lib().action(action)
	}

	public static api(): WSAPI {
		return Smartico.lib().api
	}
}

Smartico.initializeApiReady()

export { Smartico }
